import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Download, Search } from "lucide-react";
import { api, errMsg, fmtDate, money, download } from "../../lib/api";
import { useAuth, canWrite } from "../../context/AuthContext";
import RecordDialog from "./RecordDialog";

const TONES = { Closed: "bg-slate-100 text-slate-600", Done: "bg-slate-100 text-slate-600", Completed: "bg-emerald-50 text-emerald-700", Approved: "bg-emerald-50 text-emerald-700", Compliant: "bg-emerald-50 text-emerald-700", Open: "bg-sky-50 text-sky-700", Extreme: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700", Medium: "bg-amber-50 text-amber-700", Low: "bg-emerald-50 text-emerald-700", Critical: "bg-red-100 text-red-700", Major: "bg-orange-100 text-orange-700" };

export const Badge = ({ v }) => <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${TONES[v] || "bg-slate-100 text-slate-700"}`}>{v}</span>;

export function cellValue(f, rec, refLabels) {
  const v = rec[f.key];
  if (v === undefined || v === null || v === "") return "—";
  if (f.type === "date") return fmtDate(v);
  if (f.key === "total") return money(v);
  if (f.type === "ref") return refLabels[v] || "—";
  if (f.type === "select" || f.key === "risk_rating") return <Badge v={v} />;
  return String(v);
}

export default function ModulePage() {
  const { key } = useParams();
  const { modules } = useOutletContext();
  const { session } = useAuth();
  const mod = modules.find((m) => m.key === key);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [refLabels, setRefLabels] = useState({});
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();
  const writable = canWrite(session);
  const close = useCallback(() => setOpen(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/records/${key}`, { params: { q, status } });
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [key, q, status]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    setQ("");
    setStatus("");
  }, [key]);

  useEffect(() => {
    if (!mod) {
      return;
    }
    const refs = [...new Set(mod.fields.filter((f) => f.type === "ref").map((f) => f.ref))];
    Promise.all(refs.map((r) => api.get(`/records/${r}/options`).then((x) => x.data))).then((lists) => {
      const map = {};
      lists.flat().forEach((o) => { map[o.id] = `${o.ref} · ${o.title}`; });
      setRefLabels(map);
    }).catch(() => {});
  }, [mod]);

  useEffect(() => {
    const id = params.get("open");
    if (id) {
      setOpen({ id });
      params.delete("open");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const cols = useMemo(() => (mod ? mod.fields.filter((f) => f.list).slice(0, 6) : []), [mod]);
  if (!mod) return <p className="text-slate-500" data-testid="module-not-found">{modules.length ? "This module is not available." : "Loading…"}</p>;
  const statusField = mod.fields.find((f) => f.key === "status");

  return (
    <div className="max-w-7xl mx-auto" data-testid={`module-page-${key}`}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow">{mod.group}</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight" data-testid="module-title">{mod.name}</h1>
          <p className="mt-1 text-sm text-slate-600">{mod.description}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => download(`/records/${key}/export.csv`, `${key}.csv`).catch((e) => toast.error(errMsg(e)))} className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2.5 rounded-md text-sm font-semibold" data-testid="module-export-btn"><Download className="w-4 h-4" />Export CSV</button>
          <button disabled={!writable} onClick={() => setOpen({})} title={writable ? "" : "Read-only — renew your subscription to add records"} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-md text-sm font-semibold" data-testid="module-new-btn"><Plus className="w-4 h-4" />New record</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-200">
          <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${mod.name.toLowerCase()}…`} className="field pl-9" data-testid="module-search-input" /></div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="field sm:w-56" data-testid="module-status-filter">
            <option value="">All statuses</option>{statusField.options.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`register-table-${key}`}>
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3 font-semibold">Ref</th>{cols.map((f) => <th key={f.key} className="px-4 py-3 font-semibold whitespace-nowrap">{f.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpen(r)} className="border-t border-slate-100 hover:bg-sky-50/40 cursor-pointer" data-testid={`register-row-${r.ref}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{r.ref}{r.is_demo && <span className="ml-2 px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-semibold">DEMO</span>}</td>
                  {cols.map((f) => <td key={f.key} className={`px-4 py-3 ${f.key === "title" ? "font-medium text-slate-900 min-w-[200px]" : "text-slate-600"}`}>{cellValue(f, r, refLabels)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && <p className="px-4 py-12 text-center text-sm text-slate-500" data-testid="module-empty">No records found.{writable && " Click New record to add the first one."}</p>}
        </div>
      </div>
      {open && <RecordDialog mod={mod} initial={open} writable={writable} role={session.user.role} onClose={close} onSaved={load} />}
    </div>
  );
}
